import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CartService } from '../../core/services/cart.service';
import { CheckoutService } from '../../core/services/checkout.service';
import { OrderService } from '../../core/services/order.service';
import { Order } from '../../core/models/order.model';
import {
  isValidCheckoutSessionId,
  readCheckoutSessionIdFromLocation
} from '../../core/utils/checkout-return.util';

@Component({
  selector: 'app-checkout-success',
  imports: [RouterLink, DecimalPipe],
  templateUrl: './checkout-success.component.html'
})
export class CheckoutSuccessComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly cart = inject(CartService);
  private readonly checkout = inject(CheckoutService);
  private readonly orders = inject(OrderService);

  readonly order = signal<Order | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly downloading = signal(false);

  private sessionId: string | null = null;
  private clearedCart = false;

  ngOnInit(): void {
    const embedded = this.checkout.readEmbeddedSession();
    const fromRoute =
      this.route.snapshot.queryParamMap.get('session_id') ??
      this.route.snapshot.queryParamMap.get('sessionId');
    this.sessionId =
      (isValidCheckoutSessionId(fromRoute) ? fromRoute!.trim() : null) ??
      readCheckoutSessionIdFromLocation() ??
      (isValidCheckoutSessionId(embedded?.sessionId) ? embedded!.sessionId : null);

    // Always clear cart on the success route (paid or confirming).
    this.clearCartOnce();

    if (!this.sessionId) {
      this.loading.set(false);
      this.error.set('Missing order information from the payment return.');
      return;
    }

    this.loadReceipt();
  }

  downloadPdf(): void {
    if (!this.sessionId || this.downloading()) {
      return;
    }

    this.downloading.set(true);
    this.orders.downloadReceiptPdfBySession(this.sessionId).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0 || (blob.type && !blob.type.includes('pdf') && blob.type.includes('html'))) {
          this.error.set('Could not download the PDF.');
          this.downloading.set(false);
          return;
        }
        const order = this.order();
        const shortId = (order?.orderNumber || order?.id || 'order')
          .replace(/-/g, '')
          .slice(0, 8)
          .toUpperCase();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Order-${shortId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        this.downloading.set(false);
      },
      error: () => {
        this.error.set('Could not download the PDF.');
        this.downloading.set(false);
      }
    });
  }

  customerName(order: Order): string {
    return `${order.customerFirstName} ${order.customerLastName}`.trim();
  }

  private clearCartOnce(): void {
    if (this.clearedCart) {
      return;
    }
    this.cart.clear();
    this.clearedCart = true;
  }

  private loadReceipt(attempt = 0): void {
    this.loading.set(true);
    this.orders.getReceiptBySession(this.sessionId!).subscribe({
      next: (order) => {
        this.order.set(order);
        this.error.set(null);
        this.clearCartOnce();
        this.checkout.clearEmbeddedSession();

        if (order.status !== 'Paid' && attempt < 8) {
          this.loading.set(false);
          setTimeout(() => this.loadReceipt(attempt + 1), 1500);
          return;
        }

        this.loading.set(false);
      },
      error: () => {
        if (attempt < 8) {
          setTimeout(() => this.loadReceipt(attempt + 1), 1500);
          return;
        }
        this.loading.set(false);
        this.error.set('Could not load your order receipt yet. Please check your email or try again shortly.');
      }
    });
  }
}
